import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useState, useEffect, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

// Material Table Icons Imports
import AddBox from '@mui/icons-material/AddBox';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import Check from '@mui/icons-material/Check';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Clear from '@mui/icons-material/Clear';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Edit from '@mui/icons-material/Edit';
import FilterList from '@mui/icons-material/FilterList';
import FirstPage from '@mui/icons-material/FirstPage';
import LastPage from '@mui/icons-material/LastPage';
import Remove from '@mui/icons-material/Remove';
import SaveAlt from '@mui/icons-material/SaveAlt';
import Search from '@mui/icons-material/Search';
import ViewColumn from '@mui/icons-material/ViewColumn';

// Date and Time
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isBetween from 'dayjs/plugin/isBetween';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// Material UI Imports
import MaterialTable from 'material-table';
import {
  Card,
  Stack,
  Button,
  Container,
  Typography,
  IconButton,
  TableContainer,
  Snackbar,
  Alert,
  TextField,
} from '@mui/material';

// components
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';

// Main Imports
import Qoutation from '../layouts/Quotation/CreateQoutation';
import PrintPDFQoutation from '../layouts/Quotation/PrintQoutation';
import EditQoutation from '../layouts/Quotation/EditQoutation';
import Logs from '../layouts/Quotation/Logs';

// Comma Function
import muiComma from '../components/commaFunction';

// Extend dayjs with plugins
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);

// Material Table Icons Defined
const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

export default function Quotations() {
  const navigate = useNavigate();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [invoiceSnackbar, setInvoiceSnackbar] = useState(false);
  const [invoiceErrorSnackbar, setInvoiceErrorSnackbar] = useState(false);
  
  // Set default dates to last week
  const [searchFrom, setSearchFrom] = useState(dayjs().subtract(7, 'days').startOf('day'));
  const [searchTo, setSearchTo] = useState(dayjs().endOf('day'));
  
  const [quotations, setQuotations] = useState([]);
  const [allQuotations, setAllQuotations] = useState([]); // Store all quotations for filtering
  const [editData, setEditData] = useState(null);
  
  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openLogsModal, setOpenLogsModal] = useState(false);
  const [openPDFModal, setOpenPDFModal] = useState(false);

  // Get token helper function
  const getToken = () => {
    const storedToken = localStorage.getItem('userToken');
    return storedToken ? JSON.parse(storedToken) : null;
  };

  const handleLogout = () => {
    const confirmLogout = window.confirm('Your session has expired. Do you want to log out?');
    if (confirmLogout) {
      localStorage.removeItem('userToken');
      navigate('/login');
    }
  };

  // Fetch all quotations from backend
  const fetchAllQuotations = async (fromDate = searchFrom, toDate = searchTo) => {
    const token = getToken();
    if (!token) {
      alert('Token not found. Please log in again.');
      navigate('/login');
      return;
    }
  
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  
    try {
      // Use the passed date parameters
      const quotationsResponse = await fetch('https://quotationserver.aiodock.com/showQuotation', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          from: fromDate.startOf('day').toISOString(),
          to: toDate.endOf('day').toISOString(),
        }),
      });
  
      if (quotationsResponse.status === 401) {
        handleLogout();
        return;
      }
  
      const clientsResponse = await fetch('https://quotationserver.aiodock.com/showClients', {
        method: 'GET',
        headers,
      });
  
      if (clientsResponse.status === 401) {
        handleLogout();
        return;
      }
  
      const [quotationsData, clientsData] = await Promise.all([
        quotationsResponse.json(),
        clientsResponse.json()
      ]);
  
      const clientMap = {};
      clientsData.message.forEach((client) => {
        clientMap[client._id] = {
          phoneNumber: client.contactNo,
          company: client.company,
        };
      });
  
      const allProcessedQuotations = quotationsData.message.map((quotation) => {
        const clientInfo = clientMap[quotation.clientId] || {};
        return {
          ...quotation,
          phoneNumber: clientInfo.phoneNumber,
          company: clientInfo.company,
          date: dayjs(quotation.date).format('YYYY-MM-DD')
        };
      });
  
      setAllQuotations(allProcessedQuotations);
      setQuotations(allProcessedQuotations); // Set directly instead of filtering
  
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Failed to fetch data. Please try again.');
    }
  };
  // Filter quotations by date range
  const filterQuotationsByDate = (quotationsList, fromDate, toDate) => {
    const filtered = quotationsList.filter(quotation => {
      const quotationDate = dayjs(quotation.date);
      return quotationDate.isSameOrAfter(fromDate, 'day') && 
             quotationDate.isSameOrBefore(toDate, 'day');
    });
    
    setQuotations(filtered);
  };

  useEffect(() => {
    fetchAllQuotations();
  }, []);

  const handleSearch = () => {
    fetchAllQuotations(searchFrom, searchTo);
  };

  const handleResetToLastWeek = () => {
    const newFrom = dayjs().subtract(7, 'days').startOf('day');
    const newTo = dayjs().endOf('day');
    
    setSearchFrom(newFrom);
    setSearchTo(newTo);
    fetchAllQuotations(newFrom, newTo);
  };

  const handleDelete = (_id) => {
    const token = getToken();
    if (!token) {
      alert('Please log in again.');
      return;
    }

    confirmAlert({
      title: 'Delete Quotation',
      message: 'Are you sure you want to delete this quotation?',
      buttons: [
        {
          label: 'Yes',
          onClick: async () => {
            try {
              const response = await fetch(`https://quotationserver.aiodock.com/deleteQuotation/${_id}`, {
                method: 'DELETE',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
              });

              if (response.status === 401) {
                handleLogout();
                return;
              }

              if (response.ok) {
                // Remove from both allQuotations and filtered quotations
                setAllQuotations(prev => prev.filter(q => q._id !== _id));
                setQuotations(prev => prev.filter(q => q._id !== _id));
                setOpenSnackbar(true);
              } else {
                throw new Error('Failed to delete quotation');
              }
            } catch (error) {
              console.error('Delete error:', error);
              alert('Failed to delete quotation.');
            }
          }
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  const handleDuplicate = (rowData) => {
    const token = getToken();
    if (!token) {
      alert('Please log in again.');
      return;
    }

    confirmAlert({
      title: 'Duplicate Quotation',
      message: 'Are you sure you want to duplicate this quotation?',
      buttons: [
        {
          label: 'Yes',
          onClick: async () => {
            try {
              // Remove _id to allow MongoDB to create new one
              const { _id, ...duplicateData } = rowData;
              duplicateData.date = dayjs().format('YYYY-MM-DD');

              const response = await fetch('https://quotationserver.aiodock.com/addQuotation', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(duplicateData),
              });

              if (response.ok) {
                fetchAllQuotations(); // Refresh all data
                alert('Quotation duplicated successfully!');
              } else {
                throw new Error('Failed to duplicate quotation');
              }
            } catch (error) {
              console.error('Duplicate error:', error);
              alert('Failed to duplicate quotation.');
            }
          }
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  const createInvoice = async (rowData) => {
    const token = getToken();
    if (!token) {
      alert('Please log in again.');
      return;
    }

    confirmAlert({
      title: 'Create Invoice',
      message: 'Do you want to create an invoice from this quotation?',
      buttons: [
        {
          label: 'Yes',
          onClick: async () => {
            try {
              const response = await fetch('https://quotationserver.aiodock.com/addInvoice', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(rowData),
              });

              if (response.ok) {
                setInvoiceSnackbar(true);
              } else if (response.status === 401) {
                handleLogout();
              } else {
                setInvoiceErrorSnackbar(true);
              }
            } catch (error) {
              console.error('Invoice creation error:', error);
              setInvoiceErrorSnackbar(true);
            }
          }
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  // Modal handlers
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => {
    setOpenModal(false);
    fetchAllQuotations(); // Refresh data after creating new quotation
  };

  const handleOpenEditModal = (data) => {
    setEditData(data);
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    fetchAllQuotations(); // Refresh data after editing
  };

  const handleOpenLogsModal = (data) => {
    setEditData(data);
    setOpenLogsModal(true);
  };

  const handleCloseLogsModal = () => setOpenLogsModal(false);

  const handleOpenPDFModal = (data) => {
    setEditData(data);
    setOpenPDFModal(true);
  };

  const handleClosePDFModal = () => setOpenPDFModal(false);

  return (
    <>
      <Helmet>
        <title>Quotations | AIO Dock</title>
      </Helmet>

      <Container maxWidth="xl">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <TextField
                label="From"
                type="date"
                value={searchFrom.format('YYYY-MM-DD')}
                onChange={(e) => setSearchFrom(dayjs(e.target.value))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="To"
                type="date"
                value={searchTo.format('YYYY-MM-DD')}
                onChange={(e) => setSearchTo(dayjs(e.target.value))}
                InputLabelProps={{ shrink: true }}
              />
              <Button variant="contained" onClick={handleSearch} startIcon={<Search />}>
                Search
              </Button>
              <Button variant="outlined" onClick={handleResetToLastWeek}>
                Last 7 Days
              </Button>
            </Stack>
            <Button variant="contained" onClick={handleOpenModal} startIcon={<Iconify icon="eva:plus-fill" />}>
              Create Quotation
            </Button>
          </Stack>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Showing {quotations.length} quotations from {searchFrom.format('MMM D, YYYY')} to {searchTo.format('MMM D, YYYY')}
          </Typography>
        </LocalizationProvider>

        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <MaterialTable
                title={`Quotations (Last ${dayjs(searchTo).diff(searchFrom, 'days') + 1} days)`}
                icons={tableIcons}
                columns={[
                  {
                    title: 'Date',
                    field: 'date',
                    defaultSort: 'desc',
                    render: (rowData) => dayjs(rowData.date).format('DD/MM/YYYY'),
                  },
                  { title: 'Client', field: 'clientName' },
                  { title: 'Phone', field: 'phoneNumber' },
                  { title: 'Company', field: 'company' },
                  { title: 'Module', field: 'module' },
                  { title: 'Category', field: 'category' },
                  { title: 'Width', field: 'screenWidth' },
                  { title: 'Height', field: 'screenHeight' },
                  { 
                    title: 'Rate', 
                    field: 'rate', 
                    render: (rowData) => muiComma(rowData.rate) 
                  },
                  {
                    title: 'Status',
                    field: 'status',
                    render: (rowData) => (
                      <Typography
                        color={
                          rowData.status === 'Sent / WIP' ? 'purple' :
                          rowData.status === 'Win' ? 'green' :
                          rowData.status === 'Loss' ? 'error.main' : 'text.primary'
                        }
                      >
                        {rowData.status}
                      </Typography>
                    ),
                  },
                  { title: 'Remarks', field: 'remarks' },
                  {
                    title: 'Actions',
                    field: 'actions',
                    render: (rowData) => (
                      <Stack direction="row" spacing={1}>
                        <IconButton onClick={() => handleOpenEditModal(rowData)} color="primary">
                          <Iconify icon="eva:edit-fill" />
                        </IconButton>
                        <IconButton onClick={() => handleOpenPDFModal(rowData)} color="info">
                          <Iconify icon="material-symbols:print" />
                        </IconButton>
                        <IconButton onClick={() => handleOpenLogsModal(rowData)} color="success">
                          <Iconify icon="mdi:eye" />
                        </IconButton>
                        <IconButton onClick={() => handleDelete(rowData._id)} color="error">
                          <Iconify icon="eva:trash-2-outline" />
                        </IconButton>
                        <IconButton onClick={() => handleDuplicate(rowData)} color="secondary">
                          <Iconify icon="eva:copy-outline" />
                        </IconButton>
                        <IconButton onClick={() => createInvoice(rowData)} color="warning">
                          <Iconify icon="mdi:invoice" />
                        </IconButton>
                      </Stack>
                    ),
                  },
                ]}
                data={quotations}
                options={{
                  headerStyle: {
                    backgroundColor: '#2065D1',
                    color: '#FFF',
                  },
                  actionsColumnIndex: -1,
                  pageSize: 10,
                  pageSizeOptions: [10, 20, 50],
                  exportButton: true,
                  sorting: true,
                }}
              />
            </TableContainer>
          </Scrollbar>
        </Card>

        {/* Modals */}
        {openModal && <Qoutation onClose={handleCloseModal} />}
        {openEditModal && <EditQoutation data={editData} onClose={handleCloseEditModal} />}
        {openPDFModal && <PrintPDFQoutation data={editData} onClose={handleClosePDFModal} />}
        {openLogsModal && <Logs data={editData} onClose={handleCloseLogsModal} />}

        {/* Snackbars */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert severity="success" onClose={() => setOpenSnackbar(false)}>
            Quotation deleted successfully!
          </Alert>
        </Snackbar>

        <Snackbar
          open={invoiceSnackbar}
          autoHideDuration={3000}
          onClose={() => setInvoiceSnackbar(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert severity="success" onClose={() => setInvoiceSnackbar(false)}>
            Invoice created successfully!
          </Alert>
        </Snackbar>

        <Snackbar
          open={invoiceErrorSnackbar}
          autoHideDuration={3000}
          onClose={() => setInvoiceErrorSnackbar(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert severity="error" onClose={() => setInvoiceErrorSnackbar(false)}>
            Failed to create invoice. Please try again.
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
}